import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    const navigate = useNavigate();

    useEffect(() => {
        document.title = 'SLATE – Privacy Policy';
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', 'Privacy Policy for SLATE - The School Document Management System.');
        }
    }, []);

    return (
        <div className="privacy-page">
            <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
                <button 
                    onClick={() => navigate('/')} 
                    style={{ 
                        padding: '8px 16px', 
                        backgroundColor: '#1b5e20', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer', 
                        fontWeight: 'bold',
                        fontSize: '14px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                >
                    Login
                </button>
            </div>
            {/* Privacy Policy Content */}
            <main className="privacy-content">
                <div className="privacy-container">
                    <h1 className="privacy-title">SLATE — Privacy Policy</h1>
                    <p className="last-updated">Last Updated: 18 August 2026</p>

                    <section className="privacy-section">
                        <h2>1. Introduction</h2>
                        <p>Welcome to SLATE (School Lab & Activity Tracking Environment).</p>
                        <p>SLATE is an educational technology platform designed to support the management, monitoring, tracking, and reporting of educational laboratory activities, schools, students, teachers/mentors, equipment, sessions, and related educational information.</p>
                        <p>This Privacy Policy explains how information may be collected, used, stored, protected, and managed when users access and use the SLATE platform and its associated services.</p>
                        <p>By accessing or using SLATE, you acknowledge that you have read and understood this Privacy Policy.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>2. Information We Collect</h2>
                        <p>Depending on the user's role and activities on the platform, SLATE may process information such as:</p>
                        <ul>
                            <li>Name</li>
                            <li>Email address</li>
                            <li>Phone number, where applicable</li>
                            <li>User role and account information</li>
                            <li>School-related information</li>
                            <li>State, district, and location-related information</li>
                            <li>Laboratory and laboratory assignment information</li>
                            <li>Session and activity information</li>
                            <li>Student participation and registration information</li>
                            <li>Assessment-related information, where applicable</li>
                            <li>Equipment and inventory information</li>
                            <li>Reports and documents uploaded to the platform</li>
                            <li>Login and authentication-related information</li>
                            <li>Information submitted through forms available on the platform</li>
                        </ul>
                        <p>The specific information collected may vary depending on the user's role and the features available to that user.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>3. Account and User Information</h2>
                        <p>SLATE provides role-based access to different categories of users. Depending on the assigned role, users may have access to different features and information within the platform.</p>
                        <p>User account information is used primarily to:</p>
                        <ul>
                            <li>Authenticate users</li>
                            <li>Provide appropriate access to the platform</li>
                            <li>Manage user roles and permissions</li>
                            <li>Associate users with relevant schools, laboratories, districts, or other organizational units</li>
                            <li>Maintain platform security</li>
                            <li>Provide platform-related services and support</li>
                        </ul>
                        <p>Users are responsible for keeping their login credentials confidential and should immediately report any suspected unauthorized access.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>4. Educational and School Information</h2>
                        <p>SLATE may process information related to schools, laboratories, educational activities, sessions, students, teachers/mentors, equipment, and related operational activities.</p>
                        <p>Such information is used to support:</p>
                        <ul>
                            <li>Laboratory management</li>
                            <li>Activity monitoring</li>
                            <li>Session tracking</li>
                            <li>Student participation tracking</li>
                            <li>School management</li>
                            <li>Equipment management</li>
                            <li>Reporting and analytics</li>
                            <li>Administrative monitoring</li>
                            <li>Educational programme management</li>
                        </ul>
                        <p>SLATE is intended to support authorized educational and administrative users. Access to information is controlled according to the user's assigned role and permissions.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>5. How We Use Information</h2>
                        <p>Information processed through SLATE may be used for:</p>
                        <ul>
                            <li>Providing and operating the SLATE platform</li>
                            <li>Managing user accounts and permissions</li>
                            <li>Managing schools and laboratories</li>
                            <li>Recording educational activities and sessions</li>
                            <li>Monitoring participation and activities</li>
                            <li>Managing equipment and resources</li>
                            <li>Generating reports and analytics</li>
                            <li>Supporting administrative decision-making</li>
                            <li>Maintaining system security</li>
                            <li>Troubleshooting technical issues</li>
                            <li>Improving the functionality and reliability of the platform</li>
                            <li>Responding to support requests and grievances</li>
                            <li>Meeting applicable administrative and operational requirements</li>
                        </ul>
                        <p>We aim to use information only for legitimate platform, educational, administrative, operational, security, and reporting purposes.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>6. Data Access and Role-Based Permissions</h2>
                        <p>SLATE uses role-based access controls to help ensure that users can access only the information and functions relevant to their assigned responsibilities.</p>
                        <ul>
                            <li>Different users may have different levels of access, including administrative, management, mentor/teacher, school, or other authorized roles.</li>
                            <li>Access permissions may include restrictions based on organizational responsibilities, location, laboratory assignment, or other administrative configurations.</li>
                            <li>Administrators may have broader access for system management, monitoring, reporting, and support purposes.</li>
                        </ul>
                    </section>

                    <section className="privacy-section">
                        <h2>7. Data Security</h2>
                        <p>We take reasonable technical and organizational measures to protect information processed through SLATE against unauthorized access, alteration, disclosure, misuse, or loss.</p>
                        <p>Security measures may include:</p>
                        <ul>
                            <li>Role-based access control</li>
                            <li>Authentication mechanisms</li>
                            <li>Secure communication</li>
                            <li>Access restrictions</li>
                            <li>Server and application security controls</li>
                            <li>Monitoring and logging</li>
                            <li>Regular maintenance and security updates</li>
                        </ul>
                        <p>However, no internet-based system can be guaranteed to be completely secure. Users should therefore also take appropriate precautions when accessing the platform.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>8. Data Storage and Retention</h2>
                        <p>Information processed through SLATE may be stored on secure servers and retained for as long as reasonably necessary to provide the platform's services, maintain operational records, support reporting requirements, meet applicable administrative requirements, or fulfil other legitimate purposes.</p>
                        <p>Retention periods may vary depending on the type of information, its purpose, and applicable requirements.</p>
                        <p>Where information is no longer required, it may be deleted, archived, or otherwise handled in accordance with applicable policies and requirements.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>9. Sharing and Disclosure of Information</h2>
                        <p>SLATE does not intend to make user information publicly available through the platform unless such information is specifically intended to be publicly accessible.</p>
                        <p>Information may be accessed or shared with authorized personnel, institutions, administrators, service providers, or other authorized entities where necessary for:</p>
                        <ul>
                            <li>Operating SLATE</li>
                            <li>Providing technical or infrastructure services</li>
                            <li>Educational programme management</li>
                            <li>Administrative purposes</li>
                            <li>Reporting and monitoring</li>
                            <li>Security and troubleshooting</li>
                            <li>Compliance with applicable legal or governmental requirements</li>
                        </ul>
                        <p>Information may also be disclosed where required by law or by a valid request from an authorized governmental or regulatory authority.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>10. Third-Party Services</h2>
                        <p>SLATE may use third-party infrastructure, hosting, communication, analytics, authentication, or other technical services where required to operate or maintain the platform.</p>
                        <p>Where third-party services process information on behalf of SLATE, such processing is intended to be limited to the purposes necessary to provide the relevant service.</p>
                        <p>The availability and privacy practices of third-party services may be governed by their respective terms and privacy policies.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>11. Cookies and Technical Information</h2>
                        <p>SLATE may use cookies, browser storage, session information, or similar technical mechanisms where necessary for authentication, security, session management, and proper operation of the platform.</p>
                        <p>Technical information such as browser type, device information, IP address, access time, and system logs may also be processed where necessary for security, troubleshooting, and operational purposes.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>12. Student Information</h2>
                        <p>SLATE may process student-related information as part of educational laboratory activities, participation tracking, registration, assessments, and reporting.</p>
                        <p>Such information should be entered and managed only by authorized users for legitimate educational or administrative purposes.</p>
                        <p>Users and institutions responsible for entering student information should ensure that the information provided is accurate and that they have the appropriate authority to provide and manage such information.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>13. User Responsibilities</h2>
                        <p>Users are expected to:</p>
                        <ul>
                            <li>Provide accurate information</li>
                            <li>Keep account credentials confidential</li>
                            <li>Use SLATE only for authorized purposes</li>
                            <li>Avoid sharing confidential information with unauthorized persons</li>
                            <li>Follow applicable organizational policies</li>
                            <li>Report suspected security incidents or unauthorized access</li>
                            <li>Ensure that information uploaded to the platform is appropriate and authorized</li>
                        </ul>
                        <p>Unauthorized access, misuse, or attempts to compromise the platform may result in access restrictions or other appropriate action.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>14. Data Accuracy</h2>
                        <p>We aim to maintain accurate and reliable information within SLATE. However, much of the information stored on the platform is entered or managed by authorized users and institutions.</p>
                        <p>Users should therefore ensure that information they enter, update, or upload is accurate and current.</p>
                        <p>If you identify incorrect information, you should contact the appropriate administrator or authorized personnel responsible for managing that information.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>15. Grievances and Privacy Concerns</h2>
                        <p>If you have a concern regarding privacy, personal information, unauthorized access, or the handling of information on SLATE, you may contact the appropriate SLATE administrator or support team.</p>
                        <p>For platform-related concerns, please provide sufficient information to help us understand and investigate the issue.</p>
                        <p><strong>Contact Email:</strong> <a href="mailto:slate.notifier@gmail.com">slate.notifier@gmail.com</a></p>
                    </section>

                    <section className="privacy-section">
                        <h2>16. Changes to This Privacy Policy</h2>
                        <p>This Privacy Policy may be updated from time to time to reflect changes in the SLATE platform, operational requirements, security practices, or applicable requirements.</p>
                        <p>When changes are made, the updated version will be published on this page with a revised "Last Updated" date.</p>
                        <p>Users are encouraged to review this page periodically.</p>
                    </section>

                    <section className="privacy-section">
                        <h2>17. Contact Us</h2>
                        <p>For questions or concerns regarding this Privacy Policy or the SLATE platform, please contact:</p>
                        <p><strong>SLATE Support</strong></p>
                        <p><strong>Email:</strong> <a href="mailto:slate.notifier@gmail.com">slate.notifier@gmail.com</a></p>
                        <p><strong>Website:</strong> <a href="https://slate-app.eslate.info/" target="_blank" rel="noreferrer">https://slate-app.eslate.info/</a></p>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default PrivacyPolicy;
